// MUST BE SYNC WITH FILE ON API /api/src/constant/index.js

import type { Locales } from '@/services/types/api.types';

export const Constants = {
  FormComments: {
    Status: {
      Deleted: 0,
      Active: 1,
    },
    ModerationStatus: {
      Refused: 0,
      Approved: 1,
      Pending: 2,
    },
  },
  Form: {
    Type: {
      Proposal: 1,
      Question: 2,
      OpenQuestion: 3,
      Matrix: 4,
      Map: 5,
      DotVoting: 6,
    },
    Status: {
      Deleted: 0,
      Approved: 1,
      Pending: 2,
    },
    Map: {
      DefaultMaxVotes: 5,
    },
    DotVoting: {
      MeasurementType: {
        Points: 1,
        Money: 2,
      },
      Type: {
        Fixed: 1,
        Variable: 2,
      },
    },
  },
  Community: {
    Status: {
      Deleted: 0,
      Active: 1,
      PreLaunch: 2,
    },
    AccountType: {
      Customer: 1,
      Demo: 2,
      Internal: 3,
      PastCustomer: 4,
    },
    Type: {
      Public: 1,
      Private: 2,
    },
  },
  // A superadmin's entry into a customer community (COCO-3425), as the API
  // defines it in customerAccessService.
  CustomerAccess: {
    ReferenceMaxLength: 255,
    ReferenceRequiredCode: 'customer_access_reference_required',
    RuleUrl:
      'https://cocoriko.atlassian.net/wiki/spaces/Operations/pages/377257986/Access+to+customer+data+super-admin',
  },
  Consultation: {
    Status: {
      PreLaunch: 0,
      Ongoing: 1,
      Completed: 2,
      Archived: 3,
    },
    Type: {
      Public: 1,
      Private: 2,
    },
    ParticipationMode: {
      Open: 1,
      Guided: 2,
    },
    GuidedConsultation: {
      ResultsMode: {
        AllResponses: 1,
        CompletedOnly: 2,
      },
      Visibility: {
        TargetType: { Form: 'form' },
        Operator: { Equals: 'equals' },
      },
    },
    PrivateEmailLimit: 2000,
  },
  ConsultationResponse: {
    Status: {
      InProgress: 1,
      Completed: 2,
    },
  },
  User: {
    Role: {
      SUPERADMIN: 0,
      ADMIN: 1,
      EDITOR: 2,
      MODERATOR: 3,
      VIEWER: 4,
    },
  },
  languageOptions: {
    fr: { label: 'fr', value: 'fr', LongFr: 'Francais', LongEn: 'French' },
    en: { label: 'en', value: 'en', LongFr: 'Anglais', LongEn: 'English' },
  },
  Context: {
    Parent: {
      Consultation: 1,
      Section: 2,
      Form: 3,
      Module: 4,
      EventItem: 5,
    },
    Type: {
      Text: 1,
      Image: 2,
      Video: 3,
      File: 5,
      Link: 6,
    },
  },
  Section: {
    Status: {
      Deleted: 0,
      Active: 1,
      Prelaunch: 2,
    },
  },
  // A page of the Pages section. Not a custom page, whose statuses run the other way.
  Page: {
    Status: {
      Draft: 1,
      Published: 2,
    },
  },
  CustomPage: {
    Status: {
      Published: 1,
      Draft: 2,
    },
    Type: {
      About: 1,
      Content: 2,
      Link: 3,
    },
  },
  Project: {
    Visibility: {
      Public: 1,
      Private: 2,
    },
    Status: {
      PreLaunch: 0,
      Ongoing: 1,
      Completed: 2,
      Archived: 3,
    },
  },
  // Mirrors `Block` in api/src/constant/index.js. The API is the source of
  // truth: it declares `hosts` per type and enforces them on write, so the
  // clients carry only the values they read -- here, the project types.
  Block: {
    Type: {
      Text: 1,
      Video: 2,
      Consultation: 3,
      Attachments: 4,
      Faq: 5,
      Subscription: 6,
      Timeline: 7,
      ContactList: 8,
      Hero: 9,
      Event: 10,
      News: 11,
      Feedback: 12,
      QuestionAnswer: 13,
      Map: 14,
      DocumentSubmission: 15,
    },
    // Mirrors `Block.ParentType` in api/src/constant/index.js, which reserves 3.
    ParentType: { Project: 1, Page: 2 } as const,
  },
  ProjectModule: {
    QuestionAnswer: {
      Status: {
        Pending: 0,
        Answered: 1,
        Junk: 2,
      },
    },
    FeedbackEntry: {
      Status: {
        Pending: 0,
        Approved: 1,
        Refused: 2,
      },
    },
    DocumentSubmission: {
      Status: {
        Pending: 0,
        Approved: 1,
        Refused: 2,
        Superseded: 3,
      },
      State: {
        Pending: 'pending',
        Published: 'published',
        PublishedWithPendingVersion: 'publishedWithPendingVersion',
        NotPublished: 'notPublished',
        Withdrawn: 'withdrawn',
      } as const,
      Upload: {
        MaxDocumentsPerParticipant: 5,
        MaxVersionsPerDocument: 5,
        MaxFileSize: 25 * 1024 * 1024,
        AllowedMimeTypes: ['application/pdf'],
        MagicBytes: { 'application/pdf': ['%PDF-'] },
        MaxCommentLength: 500,
      },
      Review: {
        MaxNoteLength: 2000,
      },
    },
    TimelineStep: {
      Status: {
        Upcoming: 1,
        Current: 2,
        Completed: 3,
      },
    },
    EventItem: {
      Status: {
        draft: 1,
        published: 2,
      },
    },
    NewsItem: {
      Status: {
        draft: 1,
        published: 2,
      },
    },
  },
  Campaign: {
    Status: {
      Draft: 1,
      Sent: 2,
    },
  },
  MailingList: {
    Parent: {
      community: 1,
      consultation: 2,
      project: 3,
    },
    PublicationStatus: {
      draft: 1,
      published: 2,
    },
    type: {
      subscriber: 1,
      participant: 2,
    },
    DropdownType: {
      CommunitySubscribers: 1,
      ConsultationParticipants: 2,
      ConsultationSubscribers: 3,
      ProjectSubscribers: 4,
    },
  },
  Pagination: {
    DefaultRowsPerPage: 10,
  },
  Dashboard: {
    TimePeriod: {
      Yesterday: 1,
      ThisWeek: 2,
      LastSevenDays: 3,
      ThisMonth: 4,
      LastThirtyDays: 5,
    },
    Type: {
      Consultation: 1,
      Project: 2,
    },
  },
  HomepageItem: {
    Type: {
      Consultation: 1,
      Project: 2,
    },
  },
  EmailRegex:
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
  DefaultLocale: 'fr' as Locales,
};
